// JobDescriptionAddEditModal.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Container,
  Input,
  Row,
  Col,
  Label,
  FormGroup,
  FormFeedback,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { registerBase64UploadAdapter } from "helpers/ckeditorBase64UploadAdapter";
import {
  buildEmailPreviewHtml,
  isEmailLayoutHtml,
} from "helpers/ckeditorEmailPreviewHelpers";
import { bindCkeditorToolbarToPreview } from "helpers/ckeditorPreviewToolbarBridge";
import { registerMailTemplateSourceToolbarButton } from "helpers/ckeditorMailSourceButton";
import { urlEndPoint } from "helpers/GeneralHelper";
import { enableEditableMailPreview } from "helpers/mailTemplatePreviewFrame";
import {
  getClientJobDescriptionForEdit,
  upsertClientJobDescription,
} from "model/ClientJobDescriptionModel";
import { toast } from "react-toastify";
import { usePermissionContext } from "libs/context/PermissionContext";

const defaultDriverInfo = {
  experience: "",
  routeType: "",
  permalink: "",
  csaAreas: "",
  fixedOrFloater: "",
  nonCdlDrivers: "",
  l10Drivers: "",
  altVehicleDrivers: "",
  additionalInfo: "",
  startTime: "",
  milesPerDay: "",
  hoursPerDay: "",
  workSchedule: "",
  payStructure: "",
  driverPay: "",
  training: "",
  incentives: "",
  payday: "",
  vacation: "",
  otherBenefits: "",
  benefitsAdditionalInfo: "",
  fleetDescription: "",
  miscAdditionalInfo: "",
  planSubscribedTo: "",
  timeZone: "",
  questionTemplates: "",
};

const orderJobsByPreferredIds = (jobs = [], preferredIds = []) => {
  const mappedJobs = (jobs || []).map((job) => ({
    id: job?.id || job?._id || "",
    name: job?.display || job?.name || "Job",
  }));

  if (!preferredIds?.length) {
    return mappedJobs;
  }

  const jobById = new Map(
    mappedJobs.map((job) => [String(job?.id || ""), job])
  );
  const orderedJobs = [];

  preferredIds.forEach((jobId) => {
    const matchedJob = jobById.get(String(jobId || ""));
    if (matchedJob) {
      orderedJobs.push(matchedJob);
      jobById.delete(String(jobId || ""));
    }
  });

  jobById.forEach((job) => {
    orderedJobs.push(job);
  });

  return orderedJobs;
};

const formatPermalinkFromJobName = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const resolvePermalink = (permalink, jobId, jobs = []) => {
  const entered = String(permalink || "").trim();
  if (entered) {
    return entered;
  }

  const activeJob = (jobs || []).find(
    (jobItem) => String(jobItem?.id || "") === String(jobId || "")
  );
  return formatPermalinkFromJobName(activeJob?.name || "");
};

const JobDescriptionAddEditModal = ({ editableData = null, onSubmit }) => {
   const location = useLocation();
    const { jobDescriptionId, clientId } = useParams();
  const navigate = useNavigate();
  const { permissionData } = usePermissionContext() || {};
  const canUpdate = Boolean(permissionData?.RegisterJobDescriptionUpdateAccess);
  const isDuplicateMode = Boolean(location?.state?.duplicate);
  const isNewJobDescriptionFlow = ["new", "add"].includes(
    String(jobDescriptionId || "").toLowerCase()
  );
  const isSequentialFlow =
    Boolean(location?.state?.sequentialJobFlow) || isNewJobDescriptionFlow;
  const preferredJobIds = Array.isArray(location?.state?.jobIds)
    ? location.state.jobIds.map((jobId) => String(jobId || "")).filter(Boolean)
    : [];
  const currentEditableData = location?.state?.editableData || editableData;
  const cameFromClientRegisterUpsert = Boolean(location?.state?.sequentialJobFlow);
  const isEditRecordMode =
    Boolean(jobDescriptionId) &&
    !["new", "add"].includes(String(jobDescriptionId || "").toLowerCase());
  const [description, setDescription] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [bodyEditorMode, setBodyEditorMode] = useState("visual");
  const [previewSeed, setPreviewSeed] = useState("");
  const [previewRevision, setPreviewRevision] = useState(0);
  const [driverInfo, setDriverInfo] = useState({ ...defaultDriverInfo });
  const [jobDataList, setJobData] = useState([]);
  const [activeJobId, setActiveJobId] = useState("");
  const [dynamicSections, setDynamicSections] = useState([]);
  const [dynamicFieldValues, setDynamicFieldValues] = useState({});
  const [dynamicQuesDataState, setDynamicQuesDataState] = useState({});
  const [applyToAll, setApplyToAll] = useState(false);
  const [saveDtoId, setSaveDtoId] = useState("");
  const formTopRef = useRef(null);
  const editorRef = useRef(null);
  const previewFrameRef = useRef(null);
  const previewCleanupRef = useRef(null);
  const toolbarBridgeCleanupRef = useRef(null);
  const descriptionRef = useRef(description);
  const bodyEditorModeRef = useRef(bodyEditorMode);
  const showEditablePreviewRef = useRef(false);
  const onSourceToggleRef = useRef(() => {});

  // errors for validation (fieldName -> message)
  const [errors, setErrors] = useState({});
  const firstJobId = (jobDataList || [])?.[0]?.id || "";
  const lastJobId = (jobDataList || [])?.[(jobDataList || []).length - 1]?.id || "";
  const isLastJob =
    !(jobDataList || []).length || String(activeJobId || "") === String(lastJobId || "");
  const submitButtonLabel = isLastJob
    ? isSequentialFlow || isDuplicateMode || !currentEditableData
      ? "Save"
      : "Update"
    : "Next";
  const activeJobIndex = (jobDataList || []).findIndex(
    (jobItem) => String(jobItem?.id || "") === String(activeJobId || "")
  );

  const hasDescriptionContent = String(description || "").trim().length > 0;
  const showEditablePreview =
    bodyEditorMode !== "source" &&
    hasDescriptionContent &&
    isEmailLayoutHtml(description);
  showEditablePreviewRef.current = showEditablePreview;

  useEffect(() => {
    descriptionRef.current = description;
  }, [description]);

  useEffect(() => {
    bodyEditorModeRef.current = bodyEditorMode;
  }, [bodyEditorMode]);

  const syncDescriptionHtml = useCallback((html = "") => {
    const nextHtml = String(html ?? "");
    setDescription((previous) => {
      if (previous === nextHtml) return previous;
      return nextHtml;
    });
  }, []);

  const readPreviewHtmlFromFrame = useCallback(() => {
    const doc = previewFrameRef.current?.contentDocument;
    if (!doc?.body) return descriptionRef.current || "";
    return doc.body.innerHTML;
  }, []);

  const refreshVisualPreview = useCallback((html = "") => {
    const nextHtml = String(html || descriptionRef.current || "");
    setPreviewSeed(nextHtml);
    setPreviewRevision((revision) => revision + 1);
  }, []);

  onSourceToggleRef.current = (enteringSource, editor) => {
    if (enteringSource) {
      if (isEmailLayoutHtml(descriptionRef.current || "")) {
        syncDescriptionHtml(readPreviewHtmlFromFrame());
      } else if (editorRef.current) {
        syncDescriptionHtml(editorRef.current.getData());
      }
      setBodyEditorMode("source");
      return;
    }

    const html = descriptionRef.current || "";
    editor.setData(isEmailLayoutHtml(html) ? "" : html);
    refreshVisualPreview(html);
    setBodyEditorMode("visual");
  };

  useEffect(() => {
    if (!showEditablePreview) return;
    if (String(previewSeed || "").trim()) return;
    if (!String(description || "").trim()) return;
    setPreviewSeed(description);
  }, [showEditablePreview, previewSeed, description]);

  const handlePreviewFrameLoad = useCallback(() => {
    if (previewCleanupRef.current) {
      previewCleanupRef.current();
      previewCleanupRef.current = null;
    }

    previewCleanupRef.current = enableEditableMailPreview(
      previewFrameRef.current,
      syncDescriptionHtml
    );
  }, [syncDescriptionHtml]);

  useEffect(
    () => () => {
      if (previewCleanupRef.current) {
        previewCleanupRef.current();
      }
      if (toolbarBridgeCleanupRef.current) {
        toolbarBridgeCleanupRef.current();
      }
    },
    []
  );

  useEffect(() => {
    const button = document.querySelector(
      ".job-description-editor-shell [data-mail-source-btn]"
    );
    if (!button) return;

    if (bodyEditorMode === "source") {
      button.classList.add("ck-on");
      button.classList.remove("ck-off");
      return;
    }

    button.classList.add("ck-off");
    button.classList.remove("ck-on");
  }, [bodyEditorMode, editorKey]);

  const handleBodySourceChange = (event) => {
    const { value } = event.target;
    setDescription(value);
  };

  const resolveDescriptionForSubmit = () => {
    const currentDescription = descriptionRef.current || "";
    if (
      bodyEditorModeRef.current === "visual" &&
      String(currentDescription).trim() &&
      isEmailLayoutHtml(currentDescription)
    ) {
      return readPreviewHtmlFromFrame();
    }
    if (bodyEditorModeRef.current === "visual" && editorRef.current) {
      return editorRef.current.getData() || currentDescription;
    }
    return currentDescription;
  };

  const getNextJobId = () => {
    const list = jobDataList || [];
    const currentIndex = list.findIndex(
      (jobItem) => String(jobItem?.id || "") === String(activeJobId || "")
    );
    if (currentIndex >= 0 && currentIndex < list.length - 1) {
      return list[currentIndex + 1]?.id || "";
    }
    return "";
  };

  // Load editable data on open or reset on add
  useEffect(() => {
    if (currentEditableData) {
      const nextDescription = currentEditableData.description || "";
      setDescription(nextDescription);
      setPreviewSeed(nextDescription);
      setDriverInfo((prev) => ({ ...defaultDriverInfo, ...currentEditableData.driverInfo }));
    } else {
      setDescription("");
      setPreviewSeed("");
      setDriverInfo({ ...defaultDriverInfo });
    }
    setBodyEditorMode("visual");
    setEditorKey((key) => key + 1);
    setErrors({});
  }, [currentEditableData]);

  const closeModal = () => {
    if(clientId && jobDescriptionId){
    navigate(`/${urlEndPoint.ClientJobDescription}${clientId? `/${clientId}` : ''}`);
    }
    else{
    navigate(`/${urlEndPoint.jobDescription}`);
    }
  };

  const scrollToFormTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const mapDynamicQuesToSections = (dynamicQuesData) => {
    const sectionEntries = Object.entries(dynamicQuesData || {});
    return sectionEntries.map(([sectionName, fieldObj]) => ({
      id: sectionName,
      name: sectionName,
      fields: Object.keys(fieldObj || {}).map((fieldName) => ({
        id: `${sectionName}__${fieldName}`,
        name: fieldName,
        fieldType: "text",
      })),
    }));
  };

  const mapDynamicQuesToValues = (dynamicQuesData) => {
    const values = {};
    Object.entries(dynamicQuesData || {}).forEach(([sectionName, fieldObj]) => {
      Object.entries(fieldObj || {}).forEach(([fieldName, fieldValue]) => {
        values[`${sectionName}__${fieldName}`] = fieldValue || "";
      });
    });
    return values;
  };

  const resolveForEditResponseByRecordId = async (
    initialResponse,
    jobs = [],
    targetRecordId = ""
  ) => {
    const normalizedRecordId = String(targetRecordId || "").trim();
    if (!normalizedRecordId) {
      return initialResponse;
    }

    const initialSaveDto = initialResponse?.data?.saveDto || {};
    if (String(initialSaveDto?.id || "") === normalizedRecordId) {
      return initialResponse;
    }

    for (const job of jobs || []) {
      const jobId = String(job?.id || job?._id || "").trim();
      if (!jobId) continue;

      const jobResponse = await getClientJobDescriptionForEdit(clientId, jobId);
      const jobSaveDto = jobResponse?.data?.saveDto || {};
      if (String(jobSaveDto?.id || "") === normalizedRecordId) {
        return jobResponse;
      }
    }

    return initialResponse;
  };

  const loadForEditData = async (selectedJobId = "", { scrollToTop = false } = {}) => {
    if (!clientId) return;
    let response = await getClientJobDescriptionForEdit(clientId, selectedJobId);
    let responseData = response?.data || {};
    let jobs = Array.isArray(responseData?.jobs) ? responseData.jobs : [];

    if (!selectedJobId && isEditRecordMode) {
      response = await resolveForEditResponseByRecordId(
        response,
        jobs,
        jobDescriptionId
      );
      responseData = response?.data || {};
      jobs = Array.isArray(responseData?.jobs) ? responseData.jobs : [];
    }

    const saveDto = responseData?.saveDto || {};

    const mappedJobs = orderJobsByPreferredIds(jobs, preferredJobIds);
    setJobData(mappedJobs);

    const findMatchingJobId = (candidateJobId) => {
      const matchedJob = (mappedJobs || []).find(
        (jobItem) => String(jobItem?.id || "") === String(candidateJobId || "")
      );
      return matchedJob?.id || "";
    };

    let nextActiveJobId = "";
    if (selectedJobId) {
      nextActiveJobId = findMatchingJobId(selectedJobId) || selectedJobId;
    } else if (isSequentialFlow) {
      nextActiveJobId = mappedJobs?.[0]?.id || "";
    } else {
      nextActiveJobId =
        findMatchingJobId(saveDto?.jobId) ||
        mappedJobs?.[0]?.id ||
        "";
    }
    if (nextActiveJobId) {
      setActiveJobId(nextActiveJobId);
    }

    setSaveDtoId(saveDto?.id || "");
    setApplyToAll(Boolean(saveDto?.applyToAll));
    const nextDescription = saveDto?.jobDescription || "";
    setDescription(nextDescription);
    setPreviewSeed(nextDescription);
    setBodyEditorMode("visual");
    setEditorKey((key) => key + 1);
    setDriverInfo((prev) => ({
      ...prev,
      permalink: resolvePermalink(saveDto?.permalink, nextActiveJobId, mappedJobs),
    }));

    let parsedDynamicQuesData = {};
    if (typeof saveDto?.dynamicQuesData === "string") {
      try {
        parsedDynamicQuesData = JSON.parse(saveDto.dynamicQuesData || "{}");
      } catch {
        parsedDynamicQuesData = {};
      }
    } else {
      parsedDynamicQuesData = saveDto?.dynamicQuesData || {};
    }
    setDynamicQuesDataState(parsedDynamicQuesData || {});
    setDynamicSections(mapDynamicQuesToSections(parsedDynamicQuesData));
    setDynamicFieldValues(mapDynamicQuesToValues(parsedDynamicQuesData));
    setErrors({});

    if (scrollToTop) {
      requestAnimationFrame(() => scrollToFormTop());
    }
  };

  const handleDriverInfoChange = (field, value) => {
    setDriverInfo((prev) => ({ ...prev, [field]: value }));
    // clear error on change
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const handleJobTabChange = (jobId) => {
    setActiveJobId(jobId);
    loadForEditData(jobId);
  };

  const handleDynamicFieldChange = (fieldId, value) => {
    setDynamicFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const getResolvedPermalink = () =>
    resolvePermalink(driverInfo?.permalink, activeJobId, jobDataList);

  // Validate only the required fields requested by you
  const validateForm = () => {
    const newErrors = {};

    if (!getResolvedPermalink()) {
      newErrors.permalink = "Permalink is required.";
    }

    if (!(dynamicSections || []).length) {
      if (!driverInfo.experience || !driverInfo.experience.toString().trim()) {
        newErrors.experience = "Minimum required experience is required.";
      }

      if (!driverInfo.routeType || !driverInfo.routeType.toString().trim()) {
        newErrors.routeType = "Route type is required.";
      }

      if (!driverInfo.driverPay || !driverInfo.driverPay.toString().trim()) {
        newErrors.driverPay = "Driver pay is required.";
      }

      if (!driverInfo.payday || !driverInfo.payday.toString().trim()) {
        newErrors.payday = "Payday is required.";
      }
    }

    // optional: ensure questionTemplates (if provided) is a non-negative integer
    if (
      driverInfo.questionTemplates !== "" &&
      driverInfo.questionTemplates !== null &&
      !Number.isInteger(Number(driverInfo.questionTemplates))
    ) {
      newErrors.questionTemplates = "Question templates must be an integer.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildDynamicQuesData = () => {
    const output = JSON.parse(JSON.stringify(dynamicQuesDataState || {}));
    (dynamicSections || []).forEach((section) => {
      const sectionKey = section?.name || section?.id;
      if (!sectionKey) return;
      if (!output?.[sectionKey] || typeof output[sectionKey] !== "object") {
        output[sectionKey] = {};
      }
      (section?.fields || []).forEach((fieldItem) => {
        const fieldKey = fieldItem?.name || fieldItem?.id;
        if (!fieldKey) return;
        if (Object.prototype.hasOwnProperty.call(dynamicFieldValues, fieldItem?.id)) {
          output[sectionKey][fieldKey] = dynamicFieldValues?.[fieldItem?.id] || "";
        } else if (!Object.prototype.hasOwnProperty.call(output[sectionKey], fieldKey)) {
          output[sectionKey][fieldKey] = "";
        }
      });
    });
    return output;
  };

  const handleSubmit = async () => {
    if (!canUpdate) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    const currentDescription = descriptionRef.current || "";
    if (
      bodyEditorModeRef.current === "visual" &&
      String(currentDescription).trim() &&
      isEmailLayoutHtml(currentDescription)
    ) {
      syncDescriptionHtml(readPreviewHtmlFromFrame());
    } else if (bodyEditorModeRef.current === "visual" && editorRef.current) {
      syncDescriptionHtml(editorRef.current.getData());
    }

    const clientIdFromState =
      clientId ||
      location?.state?.clientId ||
      location?.state?.itemId ||
      currentEditableData?.clientId ||
      "";

    const payload = {
      clientId: clientIdFromState,
      jobId: activeJobId || "",
      applyToAll: !!applyToAll,
      permalink: getResolvedPermalink(),
      jobDescription: resolveDescriptionForSubmit(),
      dynamicQuesData: JSON.stringify(buildDynamicQuesData()),
    };

    if (!isDuplicateMode) {
      payload.id =
        saveDtoId ||
        currentEditableData?.id ||
        (jobDescriptionId && !["new", "add"].includes(String(jobDescriptionId).toLowerCase())
          ? jobDescriptionId
          : "");
    }

    const response = await upsertClientJobDescription(payload);

    if (response?.success) {
      toast.success(response?.message || "Job description saved successfully");
      if (typeof onSubmit === "function") {
        onSubmit(payload);
      }

      const nextJobId = getNextJobId();
      if (!isLastJob && nextJobId) {
        await loadForEditData(nextJobId, { scrollToTop: true });
        return;
      }

      if (cameFromClientRegisterUpsert) {
        navigate(`/${urlEndPoint.clientRegister}`);
        return;
      }

      closeModal();
      return;
    }

    // toast.error(response?.message || "Unable to save job description");
  };

  useEffect(() => {
    if (clientId) {
      loadForEditData("", { scrollToTop: isSequentialFlow });
    }
  }, [clientId]);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div
              ref={formTopRef}
              className="d-flex justify-content-between align-items-center"
            >
              <h5 className="fw-bold m-0">
                {isDuplicateMode
                  ? "DUPLICATE JOB DESCRIPTION"
                  : jobDescriptionId && !["new", "add"].includes(String(jobDescriptionId).toLowerCase())
                    ? "EDIT JOB DESCRIPTION"
                    : "ADD JOB DESCRIPTION"}
              </h5>
              {/* <Button onClick={closeModal}>Back <Button /> */}
               <Button color="light" onClick={closeModal}>Back</Button>
              
            </div>

            {(jobDataList || []).length ? (
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3 mb-3">
                {/* {isSequentialFlow && activeJobIndex >= 0 ? (
                  <span className="text-muted small me-2">
                    Job {activeJobIndex + 1} of {jobDataList.length}
                  </span>
                ) : null} */}
                {(jobDataList || []).map((jobItem) => (
                  <Button
                    key={jobItem?.id}
                    type="button"
                    className={`tab-pill-job ${
                      String(activeJobId) === String(jobItem?.id)
                        ? "tab-active-job"
                        : "tab-inactive-job"
                    }`}
                    onClick={() => handleJobTabChange(jobItem?.id)}
                  >
                    {jobItem?.name || "Job"}
                  </Button>
                ))}
              </div>
            ) : null}

            <Row className="mt-3">
              <Col md={12}>
                <FormGroup>
                  {String(activeJobId || "") === String(firstJobId || "") ? (
                    <Label check className="fw-semibold mb-3">
                      <Input
                        key={`apply-to-all-${activeJobId || "none"}-${Number(!!applyToAll)}`}
                        type="checkbox"
                        defaultChecked={!!applyToAll}
                        onChange={(e) => setApplyToAll(!!e?.target?.checked)}
                      />{" "}
                      Apply this job description to all jobs for this client
                    </Label>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <Label className="fw-semibold">Permalink *</Label>
                  <Input
                    type="text"
                    value={driverInfo.permalink}
                    invalid={!!errors.permalink}
                    onChange={(e) => handleDriverInfoChange("permalink", e.target.value)}
                  />
                  <FormFeedback>{errors.permalink}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <h6 className="fw-bold mt-3">Job Description</h6>
            <div className="mail-template-editor-page mb-4">
              <div
                className={`mail-template-editor-shell border rounded job-description-editor-shell${
                  bodyEditorMode === "source" ? " is-source-mode" : ""
                }${showEditablePreview ? " has-email-preview" : ""}`}
              >
                <CKEditor
                  key={editorKey}
                  editor={ClassicEditor}
                  data={showEditablePreview ? "" : description || ""}
                  onReady={(editor) => {
                    editorRef.current = editor;
                    registerBase64UploadAdapter(editor);
                    registerMailTemplateSourceToolbarButton(
                      editor,
                      (enteringSource, activeEditor) => {
                        onSourceToggleRef.current(enteringSource, activeEditor);
                      },
                      bodyEditorModeRef.current === "source"
                    );
                    if (toolbarBridgeCleanupRef.current) {
                      toolbarBridgeCleanupRef.current();
                    }
                    toolbarBridgeCleanupRef.current = bindCkeditorToolbarToPreview(
                      editor,
                      () => previewFrameRef.current,
                      () => showEditablePreviewRef.current,
                      (html) => {
                        syncDescriptionHtml(html);
                      }
                    );
                  }}
                  onChange={(event, editor) => {
                    if (bodyEditorModeRef.current === "source") return;
                    if (showEditablePreview) return;
                    const nextDescription = editor.getData();
                    setDescription(nextDescription);
                    if (String(nextDescription || "").trim()) {
                      setPreviewSeed(nextDescription);
                    }
                  }}
                />
                {bodyEditorMode === "source" ? (
                  <textarea
                    className="mail-template-source-textarea form-control"
                    value={description}
                    onChange={handleBodySourceChange}
                    spellCheck={false}
                  />
                ) : null}
                {showEditablePreview ? (
                  <div className="mail-template-email-preview">
                    <iframe
                      key={`job-description-preview-${editorKey}-${previewRevision}`}
                      ref={previewFrameRef}
                      title="Job description preview"
                      className="mail-template-email-preview__frame"
                      srcDoc={buildEmailPreviewHtml(previewSeed)}
                      sandbox="allow-same-origin"
                      onLoad={handlePreviewFrameLoad}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {(dynamicSections || []).length ? (
              <>
                {(dynamicSections || []).map((section) => (
                  <div key={section?.id || section?.name}>
                    <h6 className="fw-bold mt-4">{section?.name || "Section"}</h6>
                    <hr />
                    <Row>
                      {(section?.fields || []).map((fieldItem) => (
                        <Col md={6} key={fieldItem?.id || fieldItem?.name}>
                          <FormGroup>
                            <Label className="fw-semibold">{fieldItem?.name || "Field"}</Label>
                            <Input
                              type={String(fieldItem?.fieldType || "text").toLowerCase() === "textarea"
                                ? "textarea"
                                : "text"}
                              value={dynamicFieldValues?.[fieldItem?.id] || ""}
                              onChange={(e) =>
                                handleDynamicFieldChange(fieldItem?.id, e?.target?.value || "")
                              }
                            />
                          </FormGroup>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}
              </>
            ) : null}

            <div style={{ display: (dynamicSections || []).length ? "none" : "block" }}>
        {/* Driver Information */}
        <h6 className="fw-bold mt-4">Driver Information</h6>
        <hr />

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Minimum Required Experience *</Label>
              <Input
                type="select"
                value={driverInfo.experience}
                invalid={!!errors.experience}
                onChange={(e) => handleDriverInfoChange("experience", e.target.value)}
              >
                <option value="">Select</option>
                <option value="0-1 Years">0-1 Years</option>
                <option value="1-3 Years">1-3 Years</option>
                <option value="3-5 Years">3-5 Years</option>
                <option value="5+ Years">5+ Years</option>
              </Input>
              <FormFeedback>{errors.experience}</FormFeedback>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Types of Routes *</Label>
              <Input
                type="select"
                value={driverInfo.routeType}
                invalid={!!errors.routeType}
                onChange={(e) => handleDriverInfoChange("routeType", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Suburban">Suburban</option>
                <option value="Urban">Urban</option>
                <option value="Rural">Rural</option>
              </Input>
              <FormFeedback>{errors.routeType}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Areas your CSA Covers</Label>
              <Input
                type="text"
                value={driverInfo.csaAreas}
                onChange={(e) => handleDriverInfoChange("csaAreas", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Fixed Route or Floater?</Label>
              <Input
                type="select"
                value={driverInfo.fixedOrFloater}
                onChange={(e) => handleDriverInfoChange("fixedOrFloater", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Fixed">Fixed</option>
                <option value="Floater">Floater</option>
                <option value="Both">Both</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Non-CDL Drivers Needed</Label>
              <Input
                type="text"
                value={driverInfo.nonCdlDrivers}
                onChange={(e) => handleDriverInfoChange("nonCdlDrivers", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">L-10 Drivers Needed</Label>
              <Input
                type="text"
                value={driverInfo.l10Drivers}
                onChange={(e) => handleDriverInfoChange("l10Drivers", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Alternate Vehicle Drivers Needed</Label>
              <Input
                type="text"
                value={driverInfo.altVehicleDrivers}
                onChange={(e) => handleDriverInfoChange("altVehicleDrivers", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Additional Information</Label>
              <Input
                type="textarea"
                value={driverInfo.additionalInfo}
                onChange={(e) => handleDriverInfoChange("additionalInfo", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Driver Schedule */}
        <h6 className="fw-bold mt-4">Driver Schedule</h6>
        <hr />

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Start Time for Driver</Label>
              <Input
                type="time"
                value={driverInfo.startTime}
                onChange={(e) => handleDriverInfoChange("startTime", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Typical Hours Run Each Day</Label>
              <Input
                type="text"
                value={driverInfo.hoursPerDay}
                onChange={(e) => handleDriverInfoChange("hoursPerDay", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Typical Miles Driven Each Day</Label>
              <Input
                type="text"
                value={driverInfo.milesPerDay}
                onChange={(e) => handleDriverInfoChange("milesPerDay", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Work Schedules</Label>
              <Input
                type="text"
                value={driverInfo.workSchedule}
                onChange={(e) => handleDriverInfoChange("workSchedule", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Benefits */}
        <h6 className="fw-bold mt-4">Benefits</h6>
        <hr />

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Pay Structure</Label>
              <Input
                type="text"
                value={driverInfo.payStructure}
                onChange={(e) => handleDriverInfoChange("payStructure", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">How much do you pay your drivers? *</Label>
              <Input
                type="text"
                value={driverInfo.driverPay}
                invalid={!!errors.driverPay}
                onChange={(e) => handleDriverInfoChange("driverPay", e.target.value)}
              />
              <FormFeedback>{errors.driverPay}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Training</Label>
              <Input
                type="text"
                value={driverInfo.training}
                onChange={(e) => handleDriverInfoChange("training", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Incentives</Label>
              <Input
                type="text"
                value={driverInfo.incentives}
                onChange={(e) => handleDriverInfoChange("incentives", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Payday *</Label>
              <Input
                type="text"
                value={driverInfo.payday}
                invalid={!!errors.payday}
                onChange={(e) => handleDriverInfoChange("payday", e.target.value)}
              />
              <FormFeedback>{errors.payday}</FormFeedback>
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Vacation / Sick Time</Label>
              <Input
                type="text"
                value={driverInfo.vacation}
                onChange={(e) => handleDriverInfoChange("vacation", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Other Benefits</Label>
              <Input
                type="text"
                value={driverInfo.otherBenefits}
                onChange={(e) => handleDriverInfoChange("otherBenefits", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Additional Information</Label>
              <Input
                type="text"
                value={driverInfo.benefitsAdditionalInfo}
                onChange={(e) => handleDriverInfoChange("benefitsAdditionalInfo", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Miscellaneous */}
        <h6 className="fw-bold mt-4">Miscellaneous</h6>
        <hr />

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Trucks (Describe your fleet)</Label>
              <Input
                type="text"
                value={driverInfo.fleetDescription}
                onChange={(e) => handleDriverInfoChange("fleetDescription", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Additional Information</Label>
              <Input
                type="text"
                value={driverInfo.miscAdditionalInfo}
                onChange={(e) => handleDriverInfoChange("miscAdditionalInfo", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* User Account */}
        <h6 className="fw-bold mt-4">User Account</h6>
        <hr />

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Plan Subscribed To</Label>
              <Input
                type="text"
                value={driverInfo.planSubscribedTo}
                onChange={(e) => handleDriverInfoChange("planSubscribedTo", e.target.value)}
              />
            </FormGroup>
          </Col>

          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Time Zone</Label>
              <Input
                type="text"
                value={driverInfo.timeZone}
                invalid={!!errors.timeZone}
                onChange={(e) => handleDriverInfoChange("timeZone", e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label className="fw-semibold">Question Templates</Label>
              <Input
                type="number"
                value={driverInfo.questionTemplates}
                invalid={!!errors.questionTemplates}
                onChange={(e) => handleDriverInfoChange("questionTemplates", e.target.value)}
              />
              <FormFeedback>{errors.questionTemplates}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>
        </div>

        {/* Actions — Save/Next only when RegisterJobDescriptionUpdateAccess */}
        {canUpdate ? (
          <div className="text-end mt-4">
            <Button color="primary" onClick={handleSubmit}>
              {submitButtonLabel}
            </Button>
          </div>
        ) : null}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default JobDescriptionAddEditModal;
